/**
 * Where the signed-in session lives — and why it moved.
 *
 * <p>Tokens used to sit in sessionStorage, which is PER TAB. That was invisible while the console
 * was one browser tab, and became a lockout the day it turned into an app: an installed PWA window,
 * a window opened by tapping an OS notification, a link opened in a new tab — every one of them is
 * a fresh browsing context with an empty sessionStorage, so ProtectedRoute sent the operator to the
 * login page while their other tab was still signed in. "It keeps throwing me out." localStorage is
 * shared by every tab and window of the origin (and its `storage` event keeps them in step), which is
 * what "signed in to the console" has to mean now.
 */

export const AUTH_KEYS = [
    "access-token",
    "refresher-token",
    "role",
    "workspace-id",
    "npe.must-change-password",
    "npe.must-change-password.ack",
    "npe-push-offered",
];

/**
 * Remove the session and nothing else — device preferences (sound, nav) stay.
 *
 * <p>From BOTH stores. The old per-tab copy has to go too: while it survived, every reload of a
 * tab that once held a session found it there and copied it back (see migrateLegacySession), so a
 * session that had just been ended for being dead was alive again a moment later.
 */
export function clearAuthSession() {
    for (const k of AUTH_KEYS) {
        try {
            localStorage.removeItem(k);
        } catch (e) { /* storage denied */ }
        try {
            sessionStorage.removeItem(k);
        } catch (e) { /* storage denied */ }
    }
}

/** A JWT whose `exp` has passed. Anything undecodable is left to the server to judge. */
function isExpiredJwt(token) {
    try {
        const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
        return typeof payload.exp === "number" && payload.exp * 1000 < Date.now();
    } catch (e) {
        return false;
    }
}

/**
 * Carry a session over from the old per-tab store, once, so nobody signed in before this change is
 * asked to sign in again by it. Runs at boot, before anything reads the tokens.
 *
 * <p>A MOVE, not a copy — and only of a session that can still work. The first version copied and
 * left the original behind, which turned into a one-second loop on the admin's own screen: a
 * dead session was ended and the page sent to /login, the reload copied the same dead tokens back
 * from sessionStorage, /login saw a session and forwarded to the dashboard, whose first request was
 * refused again. A refresh token that has already expired is dropped instead of moved, because
 * moving it only buys one refused request before the same sign-in.
 */
export function migrateLegacySession() {
    try {
        const legacy = sessionStorage.getItem("access-token");
        const legacyRefresh = sessionStorage.getItem("refresher-token");
        if (!legacy && !legacyRefresh) return;
        const usable = legacy && legacyRefresh && !isExpiredJwt(legacyRefresh);
        if (usable && !localStorage.getItem("access-token")) {
            for (const k of AUTH_KEYS) {
                const v = sessionStorage.getItem(k);
                if (v !== null) localStorage.setItem(k, v);
            }
        }
        // Either moved or worthless: in both cases it must not be found here again.
        for (const k of AUTH_KEYS) sessionStorage.removeItem(k);
    } catch (e) { /* storage denied — they will sign in */ }
}
